import { useEffect, useState, useCallback, useRef } from 'react';
import { createPaginationContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';

// components
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';

// helpers
import { flattenConnectionNodes } from '../../utils/relayUtils';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    SIMILAR_ITEMS_LIST,
    VISUALLY_SIMILAR_ITEMS_LIST,
    trackViewMoreVisuallySimilarClick,
    trackViewMoreRelatedItemsClick,
    trackViewLessRelatedItemsClick,
} from '../tracking/similarItemsTracking';

// messages
import {
    youMayAlsoLike,
    visuallySimilar,
    viewMore,
    viewLess,
    viewAllVisuallySimilar,
} from '../pdpMessages';

import styles from './PdpRespSimilarItemsFooter.scss';

const MAX_VISUALLY_SIMILAR_GRID_ITEMS = 4;

const removeDupeItems = (ids, visuallySimilarGridItems) => {
    return flattenConnectionNodes(visuallySimilarGridItems)
        .filter(item => !ids.includes(item.serviceId))
        .splice(0, MAX_VISUALLY_SIMILAR_GRID_ITEMS);
};

const PAGE_SIZE = 8;
const ITEMS_PER_ROW = 4;

const handleGridScroll = tileRef => {
    window.scrollTo(
        window.scrollX,
        window.scrollY - (PAGE_SIZE / ITEMS_PER_ROW) * tileRef?.current?.clientHeight
    );
};

export const PdpRespSimilarItemsFooterComponent = ({
    currency,
    viewer,
    user,
    item,
    relayVariables,
    scrollToRef,
    relay,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const gridItemWrapperRef = useRef();
    useEffect(() => {
        const { isArt } = item;
        const { isClient } = relayVariables;
        const { visuallySimilarGridItems } = viewer;
        // TODO: when we will update relay worth revisiting if client check is still needed
        // At the moment If refetch happens and in the meantime relay vars changes, data gets disposed
        // more info: https://github.com/facebook/relay/blob/1.4.1-manual-patch/packages/react-relay/modern/ReactRelayRefetchContainer.js#L114-L118
        if (isClient && isArt && !visuallySimilarGridItems) {
            relay.refetchConnection(PAGE_SIZE, null, { isArt, count: PAGE_SIZE });
        }
    }, [item, relay, relayVariables, viewer]);

    const { serviceId: userId } = user || {};
    useEffect(() => {
        if (userId) {
            // reset pagination container when user logs in to prevent broken pagination state
            setIsExpanded(false);
            setHasFetched(false);
        }
    }, [userId]);

    const { visuallySimilarGridItems, similarItems } = viewer || {};
    const { serviceId: itemId } = item;

    const similarItemsIds = flattenConnectionNodes(similarItems).map(node => node.serviceId);

    const sharedItemsGridProps = {
        relayVariables: {
            userId: relayVariables.userId,
            isClient: relayVariables.isClient,
        },
        item,
        viewer,
        currency,
        user,
        favoritesLocation: locations.SIMILAR_ITEM_GRID,
    };
    const getButtonState = useCallback(
        function () {
            if (isLoading) {
                return <Spinner size="tiny" containerClass={styles.spinner} />;
            } else if (isExpanded) {
                return viewLess;
            } else {
                return viewMore;
            }
        },
        [isLoading, isExpanded]
    );
    const onViewMoreClick = useCallback(
        function () {
            if (isExpanded) {
                trackViewLessRelatedItemsClick();
            } else {
                trackViewMoreRelatedItemsClick();
            }
            if (relay.hasMore() && !hasFetched) {
                setIsLoading(true);
                relay.loadMore(PAGE_SIZE, () => {
                    setIsLoading(false);
                    setIsExpanded(!isExpanded);
                    setHasFetched(true);
                });
            } else if (hasFetched) {
                setIsExpanded(!isExpanded);
                if (isExpanded) {
                    handleGridScroll(gridItemWrapperRef);
                }
            }
        },
        [isExpanded, relay, hasFetched]
    );

    const similarItemNodes = flattenConnectionNodes(similarItems);
    const similarGridItems = (
        <>
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                header={youMayAlsoLike}
                scrollToRef={scrollToRef}
                gridLocation={SIMILAR_ITEMS_LIST}
                similarItems={isExpanded ? similarItemNodes : similarItemNodes.slice(0, PAGE_SIZE)}
                gridItemWrapperRef={gridItemWrapperRef}
            />
            <div className={`primary-viewport-width ${styles.wrapper}`}>
                <Button
                    className={styles.button}
                    onClick={onViewMoreClick}
                    size="small"
                    type="secondary"
                >
                    {getButtonState()}
                </Button>
            </div>
        </>
    );

    const visuallySimillarItems = !!visuallySimilarGridItems?.edges?.length && (
        <>
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                gridHeaderClass={styles.visuallySimilarHeader}
                header={visuallySimilar}
                subHeader={
                    <div className={`primary-viewport-width ${styles.wrapper}`}>
                        <Link
                            onClick={() => trackViewMoreVisuallySimilarClick()}
                            href={`/similar/?item=${itemId}`}
                            className={styles.viewMoreLink}
                        >
                            {viewAllVisuallySimilar}
                        </Link>
                    </div>
                }
                gridLocation={VISUALLY_SIMILAR_ITEMS_LIST}
                similarItems={removeDupeItems(similarItemsIds, visuallySimilarGridItems)}
            />
        </>
    );

    return (
        <div className={styles.container}>
            {similarGridItems}
            {visuallySimillarItems}
        </div>
    );
};

PdpRespSimilarItemsFooterComponent.propTypes = {
    currency: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    relay: PropTypes.object,
    scrollToRef: PropTypes.object.isRequired,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool,
    }).isRequired,
};

const connectionConfig = {
    direction: 'forward',
    getConnectionFromProps(props) {
        return props.viewer && props.viewer.similarItems;
    },
    getFragmentVariables(prevVars, totalCount) {
        return {
            ...prevVars,
            count: totalCount,
        };
    },
    getVariables({ relayVariables }, { count, cursor }, fragmentVariables) {
        return {
            count,
            cursor,
            isArt: fragmentVariables.isArt,
            itemId: fragmentVariables.itemId,
            isTrade: relayVariables.isTrade,
        };
    },
    query: graphql`
        query PdpRespSimilarItemsFooterQuery(
            $isArt: Boolean!
            $itemId: String!
            $count: Int
            $cursor: String
            $isTrade: Boolean!
        ) {
            viewer {
                ...PdpRespSimilarItemsFooter_viewer
                @arguments(isArt: $isArt, count: $count, cursor: $cursor)
            }
        }
    `,
};

export const PdpRespSimilarItemsFooter = createPaginationContainer(
    PdpRespSimilarItemsFooterComponent,
    {
        viewer: graphql`
            fragment PdpRespSimilarItemsFooter_viewer on Viewer
            @argumentDefinitions(
                isArt: { type: "Boolean!", defaultValue: false }
                count: { type: "Int", defaultValue: 8 }
                cursor: { type: "String" }
            ) {
                visuallySimilarGridItems: visualSearch(itemPk: $itemId, first: 6)
                @include(if: $isArt) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
                similarItems: moreLikeThisItem(
                    first: $count
                    after: $cursor
                    seller: EXCLUDE
                    itemId: $itemId
                ) @connection(key: "PdpRespSimilarItemsFooter_similarItems") {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                    pageInfo {
                        hasNextPage
                        startCursor
                        endCursor
                    }
                    totalResults
                }
                ...PdpRespSimilarItemsGrid_viewer
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemsFooter_user on User {
                serviceId
                ...PdpRespSimilarItemsGrid_user
            }
        `,
        item: graphql`
            fragment PdpRespSimilarItemsFooter_item on Item {
                isArt
                serviceId
                ...PdpRespSimilarItemsGrid_item
            }
        `,
    },
    connectionConfig
);
